import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '../config';

const Register = () => {
  React.useEffect(() => {
    document.body.classList.add('matrix-page');   // mantiene el estilo Matrix oscuro
    return () => document.body.classList.remove('matrix-page');
  }, []);
  const [name, setName] = useState('');
  // Link de referido: /register?ref=AEG-XXXX rellena el código automáticamente
  const [referralCode, setReferralCode] = useState(
    new URLSearchParams(window.location.search).get('ref') || '');
  const [emailCode, setEmailCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const enviarCodigo = async () => {
    if (!email) { alert('Ingresa tu correo primero'); return; }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/request-code?email=${encodeURIComponent(email)}`);
      setCodeSent(true);
      if (res.data?.modo === 'fallback') {
        alert(`Correo no disponible. Tu código es: ${res.data.codigo}\n(introdúcelo en el campo o déjalo vacío para omitir)`);
      } else if (res.data?.modo === 'desarrollo') {
        alert('Código generado (sin SMTP). Puedes dejar el campo vacío y registrarte directamente.');
      } else {
        alert('Código de 6 dígitos enviado a tu correo');
      }
    } catch (e) {
      alert('No se pudo conectar con el servidor. Puedes dejar el campo de código vacío y registrarte igualmente.');
    }
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (emailCode) {
        await axios.post(`${BACKEND_URL}/api/auth/verify-code?email=${encodeURIComponent(email)}&code=${emailCode}`);
      }
      await register(email, password, name, referralCode);
      toast.success('Registration successful!');
      
      // Check if user is admin
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#00D4FF] mb-2">AEGIS IA</h1>
          <p className="text-[#A1A1AA]">Multi-Exchange Trading Platform</p>
        </div>

        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Create Account</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password-input"
                className="mt-1"
              />
            </div>

            <div className="space-y-2">
            <Label className="text-[#A1A1AA]">Verificación de correo</Label>
            <div className="flex gap-2">
              <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value)}
                placeholder="Código de 6 dígitos" maxLength={6}
                className="bg-[#1E1E1E] border-[#27272A] text-white" />
              <button type="button" onClick={enviarCodigo}
                className="px-3 rounded bg-emerald-600 text-white text-xs whitespace-nowrap">
                {codeSent ? 'Reenviar' : 'Enviar código'}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {referralCode ? (
              <div className="bg-[#10b981]/10 border border-[#10b981]/30 rounded p-2 text-center">
                <p className="text-[#10b981] text-xs font-medium">Referido por: <span className="text-white font-bold">{referralCode}</span></p>
              </div>
            ) : (
              <>
                <Label className="text-[#A1A1AA]">Código de referido (opcional)</Label>
                <Input value={referralCode} onChange={(e) => setReferralCode(e.target.value)}
                  placeholder="AEG-XXXXXXXX" className="bg-[#1E1E1E] border-[#27272A] text-white" />
              </>
            )}
          </div>

          <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full bg-[#00D4FF] text-black hover:bg-[#33DDFF] font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm shadow-[0_0_15px_rgba(0,212,255,0.3)] transition-all"
            >
              {loading ? 'Creating...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-sm text-[#A1A1AA] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#00D4FF] hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;